import type { DemoWord } from '../../api/demo'
import CEFRBadge from '../shared/CEFRBadge'

interface DemoResultListProps {
  words: DemoWord[]
  /** ISO-639-1 code of the detected language — used for future extensibility. */
  detectedLanguage: string
  /** When true, items are visually muted to signal they're placeholder data. */
  isPreview?: boolean
}

/**
 * Reusable result display for extracted vocabulary words.
 * Decoupled from the input source so Phase 7 (OCR) can feed the same display.
 */
export function DemoResultList({ words, isPreview = false }: DemoResultListProps) {
  if (words.length === 0) {
    return (
      <p
        style={{
          fontSize: 13,
          color: 'var(--text-secondary)',
          textAlign: 'center',
          padding: '16px 0',
          margin: 0,
        }}
      >
        No vocabulary words found.
      </p>
    )
  }

  return (
    <div
      data-demo-results
      style={{ opacity: isPreview ? 0.6 : 1, transition: 'opacity 0.2s' }}
    >
      {words.map((w, i) => (
        <div
          key={w.lemma}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '7px 0',
            borderBottom:
              i < words.length - 1 ? '1px solid var(--border)' : 'none',
          }}
        >
          {/* Lemma */}
          <span
            style={{
              flex: 1,
              fontSize: 14,
              fontWeight: 500,
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-content)',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {w.lemma}
          </span>

          {/* CEFR badge */}
          {w.cefr_level && <CEFRBadge level={w.cefr_level} size="sm" />}

          {/* Translation */}
          {w.translation && (
            <span
              style={{
                fontSize: 13,
                color: 'var(--text-secondary)',
                fontStyle: 'italic',
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 110,
                flexShrink: 0,
              }}
            >
              {w.translation}
            </span>
          )}
        </div>
      ))}
    </div>
  )
}
